import { motion } from "framer-motion";
import { ArrowUpRight, Sailboat, Utensils, ChefHat } from "lucide-react";
import { useLanguage } from "@/i18n";

const Experiences = () => {
  const { t } = useLanguage();

  const items = [
    {
      key: "boat",
      icon: Sailboat,
      title: t.experiences.items.boat.title,
      lead: t.experiences.items.boat.lead,
      duration: t.experiences.items.boat.duration,
      href: "https://www.getyourguide.com/isola-delle-femmine-l191648/",
      // Warm mediterranean sea gradient
      gradient: "linear-gradient(135deg, #0D253A 0%, #2E6FAF 45%, #64B3D9 80%, #F5D9B3 100%)",
    },
    {
      key: "streetfood",
      icon: Utensils,
      title: t.experiences.items.streetfood.title,
      lead: t.experiences.items.streetfood.lead,
      duration: t.experiences.items.streetfood.duration,
      href: "https://www.getyourguide.com/palermo-l395/street-food-tc95/",
      // Sicilian sunset gradient (terracotta / arancione)
      gradient: "linear-gradient(135deg, #4A1A0F 0%, #A3502F 45%, #C86D51 80%, #EFB98A 100%)",
    },
    {
      key: "cooking",
      icon: ChefHat,
      title: t.experiences.items.cooking.title,
      lead: t.experiences.items.cooking.lead,
      duration: t.experiences.items.cooking.duration,
      href: "https://www.getyourguide.com/palermo-l395/cooking-tc98/",
      // Olive / harvest gradient (verde-oro Sicilia)
      gradient: "linear-gradient(135deg, #1F2E1A 0%, #4A5B32 45%, #8A9A5B 80%, #E4D5A7 100%)",
    },
  ];

  return (
    <section
      id="esperienze"
      data-testid="experiences-section"
      className="py-20 lg:py-32 px-6 sm:px-12 lg:px-20 bg-[#0D253A] text-[#F8F5F0] relative overflow-hidden"
    >
      {/* subtle grain / atmospheric light */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none [background:radial-gradient(circle_at_15%_20%,rgba(200,109,81,0.7),transparent_45%),radial-gradient(circle_at_85%_70%,rgba(248,245,240,0.5),transparent_50%)]" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-end gap-10 lg:gap-16 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] mb-4">
              {t.experiences.eyebrow}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
              {t.experiences.title}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 text-base sm:text-lg text-[#F8F5F0]/70 leading-relaxed"
          >
            {t.experiences.text}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.a
                key={it.key}
                href={it.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`experience-${it.key}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                whileHover={{ y: -6 }}
                className="group relative flex flex-col bg-[#0f2b45] rounded-3xl overflow-hidden border border-white/10 transition-colors hover:border-[#C86D51]/60"
              >
                <div
                  className="relative aspect-[4/3] overflow-hidden flex items-center justify-center"
                  style={{ background: it.gradient }}
                >
                  {/* atmospheric texture */}
                  <div className="absolute inset-0 opacity-30 mix-blend-overlay [background:radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.5))]" />
                  {/* Big icon */}
                  <Icon
                    className="w-24 h-24 sm:w-28 sm:h-28 text-[#F8F5F0] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                    strokeWidth={1.1}
                  />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#F8F5F0]/90 bg-[#0D253A]/60 backdrop-blur px-3 py-1.5 rounded-full">
                    {it.duration}
                  </span>
                  <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F8F5F0] flex items-center justify-center text-[#0D253A] transition-transform duration-500 group-hover:rotate-45">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <div className="p-6 flex flex-col gap-2 flex-1">
                  <h3 className="font-display text-xl leading-snug">{it.title}</h3>
                  <p className="text-sm text-[#F8F5F0]/65 leading-relaxed">{it.lead}</p>
                  <span className="mt-3 text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] group-hover:text-[#F8F5F0] transition-colors">
                    {t.experiences.cta}
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>

        <p className="mt-10 text-xs font-mono-alt text-[#F8F5F0]/40 max-w-3xl">
          {t.experiences.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default Experiences;
